<script setup lang="ts">
import DateRangePickerWithPresets from "@/ui/components/date/DateRangePickerWithPresets.vue";
import { Ref, ref } from "vue";
import { DateRange, DateRangeParsed } from "@/ui/components/date/types";
import dayjs from "dayjs";
import { parseBound } from "@/ui/components/date/dateRangeValidation";

const periodSelection = defineModel<App.Data.Explorer.PeriodSelectionDTO>();

const emit = defineEmits<{
	(e: "date-validation", value: boolean): void;
	(e: "close"): void;
	(
		e: "update:modelValue",
		value: {
			from: string;
			to: string;
			from_parsed: Date;
			to_parsed: Date;
		}
	): void;
}>();
interface ComponentProps {
	showPresets: boolean;
	showApply?: boolean;
	nullable?: boolean;
}

const props = withDefaults(defineProps<ComponentProps>(), {
	showPresets: true,
	showApply: true,
	nullable: true,
});

const tmpPeriod: Ref<DateRange> = ref({
	from: periodSelection.value?.from ?? "now",
	to: periodSelection.value?.to ?? "now",
});

// Resolved from the expressions rather than the stored parsed bounds, which go
// stale for a dynamic period (i.e. now-7d/d).
const tmpPeriodParsed: Ref<DateRangeParsed> = ref({
	from: parseBound(tmpPeriod.value.from) ?? dayjs(new Date()),
	to: parseBound(tmpPeriod.value.to, true) ?? dayjs(new Date()),
});

const applyDatesToStore = () => {
	emit("update:modelValue", {
		from: tmpPeriod.value.from,
		to: tmpPeriod.value.to,
		from_parsed: tmpPeriodParsed.value.from.toDate(),
		to_parsed: tmpPeriodParsed.value.to.toDate(),
	});
};
</script>

<template>
	<date-range-picker-with-presets
		v-model="tmpPeriod"
		v-model:parsed="tmpPeriodParsed"
		@apply-dates="applyDatesToStore"
		@close="emit('close')"
		@date-validation="(value) => emit('date-validation', value)"
		:show-presets="props.showPresets"
		:show-apply="props.showApply"
		:nullable="props.nullable"
	>
		<template #aboveSubmitButton>
			<slot name="aboveSubmitButton" />
		</template>
	</date-range-picker-with-presets>
</template>

<style lang="scss"></style>
